import config from '../config/api';
import logger from '../logging/logger';

/**
 * TradingView chart configuration for symbol-based charts
 */
export interface TradingViewChartConfig {
  symbol: string;
  comparison_symbols?: string[];
  timeframe?: string;
  period?: string;
  chart_type?: 'single' | 'comparison' | 'overlay';
  theme?: 'light' | 'dark';
  style?: string;
  indicators?: string[];
  message?: string;
}

/**
 * Custom 2D chart configuration for analytics/time series data
 * data_id is always a list (even for single dataset)
 */
export interface Custom2DChartConfig {
  chart_type: 'custom_2d';
  title: string;
  value_label?: string;      // Label for the y-axis
  data_id?: string[];        // List of data references (always a list)
  series_labels?: string[];  // Labels for each series in legend (matches data_id length)
  reference_lines?: Array<{ value: number; label?: string; color?: string }>;  // Horizontal reference lines
  data?: {                   // Embedded data (legacy, for backward compatibility)
    Dates: string[];
    [key: string]: string[] | number[] | string | undefined;
  };
}

/**
 * 3D Vol surface data structure (embedded or fetched via data_id)
 */
export interface VolSurface3DData {
  DTE: number[];                 // Days to expiration (x-axis)
  Strike?: number[];             // Strike prices (y-axis, when axis_type='strike')
  Delta?: number[];              // Delta values (y-axis, when axis_type='delta')
  StrikeMidIV?: number[][];      // Mid IV matrix for strike-based surface
  DeltaMidIV?: number[][];       // Mid IV matrix for delta-based surface
  MidIV?: number[][];            // Legacy: single MidIV matrix
  Title?: string;                // Chart title for display
  [key: string]: number[] | number[][] | string | undefined;
}

/**
 * 3D Volatility Surface chart configuration
 * Supports both embedded data (legacy) and data_id reference (context optimized)
 */
export interface VolSurface3DChartConfig {
  chart_type: 'vol_surface_3d';
  title: string;
  axis_type: 'strike' | 'delta';  // Whether y-axis is strike or delta
  data?: VolSurface3DData;        // Embedded data (legacy)
  data_id?: string;               // Reference to stored data (context optimized)
}

/**
 * Regression chart configuration
 * Displays regression analysis results with coefficients, R², and statistics
 * Uses data_id reference (context optimized) - no embedded data
 */
export interface RegressionChartConfig {
  chart_type: 'regression';
  title: string;
  data_id: string;  // Reference to stored regression results
}

/**
 * Regression data structure fetched via data_id
 */
export interface RegressionData {
  intercept: number;
  slopes: number[];
  r_squared: number;
  std_errors: number[];
  conf_intervals: Array<[number, number]>;
  correlation_matrix: number[][];
  y_symbol: string;  // Y axis symbol (dependent variable)
  x_symbols: string[];  // X axes symbols (independent variables)
  y_field: string;  // Y field name (e.g., "mid_price", "ask", "values")
  x_fields: string[];  // X field names (e.g., ["implied_vol"])
  y_actual: number[];  // Actual Y values for scatter plot
  y_predicted: number[];  // Predicted Y values for regression line
  x_values: number[];  // X values (first independent variable) for scatter plot
}

/**
 * Union type for all chart configurations
 */
export type ChartConfig = TradingViewChartConfig | Custom2DChartConfig | VolSurface3DChartConfig | RegressionChartConfig;

export interface ChartReference {
  chart_id: string;
  config?: ChartConfig;
  created_at?: string;
  last_used_at?: string;
}

/**
 * Tool data format returned from API for 2D charts.
 * Contains `dates` (or `Dates`) array and dynamic value keys (e.g., 'returns', 'ma_5', 'prices').
 * Use getToolDataValueKey() to find the value key dynamically.
 *
 * For merged multi-trace data, uses uppercase `Dates` and each series as a separate key.
 */
export interface ToolDataResponse {
  dates?: string[];   // API format (lowercase)
  Dates?: string[];   // Chart format (uppercase) - used by mergeDataByDate
  [key: string]: string[] | number[] | undefined;
}

/**
 * Reserved keys that represent date/time axis or metadata (not data series).
 * Includes 'date' (singular) for LLM resilience - some models may output singular form.
 */
const DATE_RESERVED_KEYS = ['dates', 'Dates', 'date', 'Date', 'Description', 'description'];

/**
 * Normalize tool data to use canonical 'dates' key.
 * Handles LLM inconsistency where 'date' (singular) may be used instead of 'dates'.
 * Logs a warning when normalization occurs for visibility.
 */
export function normalizeToolData(data: ToolDataResponse): ToolDataResponse {
  // Check if 'date' (singular) exists but 'dates'/'Dates' don't
  const hasDateSingular = 'date' in data || 'Date' in data;
  const hasDatePlural = 'dates' in data || 'Dates' in data;

  if (hasDateSingular && !hasDatePlural) {
    const singularKey = 'date' in data ? 'date' : 'Date';
    logger.warn(
      `ChartService: Data uses '${singularKey}' instead of 'dates'. Normalizing for chart compatibility.`,
      { originalKeys: Object.keys(data) }
    );
    // Create normalized copy: extract dates value, then build new object without singular key
    const datesValue = data[singularKey] as string[];
    const { [singularKey]: _removed, ...rest } = data as Record<string, unknown>;
    return { ...rest, dates: datesValue } as ToolDataResponse;
  }
  return data;
}

/**
 * Get the value key from tool data (any array key that's not a reserved key).
 */
export function getToolDataValueKey(data: ToolDataResponse): string | null {
  const valueKey = Object.keys(data).find(
    key => !DATE_RESERVED_KEYS.includes(key) && Array.isArray(data[key])
  );
  return valueKey ?? null;
}

/**
 * Get the values array from tool data (finds the first array that's not 'dates' or 'Dates').
 */
export function getToolDataValues(data: ToolDataResponse): number[] | undefined {
  const valueKey = getToolDataValueKey(data);
  if (!valueKey) return undefined;
  return data[valueKey] as number[] | undefined;
}

/**
 * Tool data format returned from API for 3D vol surface charts
 */
export interface VolSurfaceToolDataResponse {
  DTE: number[];
  Strike: number[];
  Delta: number[];
  StrikeMidIV: number[][];
  DeltaMidIV: number[][];
}

export class ChartService {
  private baseUrl = config.apiUrl;
  private chartCache = new Map<string, ChartConfig>();
  private toolDataCache = new Map<string, ToolDataResponse>();

  /**
   * Retrieve chart configuration from session storage
   */
  async getChartConfig(
    sessionId: string, 
    chartId: string, 
    userEmail: string, 
    token: string
  ): Promise<ChartConfig | null> {
    try {
      // Check cache first
      const cacheKey = `${sessionId}:${chartId}`;
      if (this.chartCache.has(cacheKey)) {
        return this.chartCache.get(cacheKey)!;
      }

      // Get user UUID from email
      const userResponse = await fetch(`${this.baseUrl}/user/email/${encodeURIComponent(userEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!userResponse.ok) {
        logger.error('Failed to get user from database');
        return null;
      }
      
      const userData = await userResponse.json();
      const userUuid = userData.uuid;

      // Fetch chart configuration
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/chart/${chartId}?user_id=${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`Chart configuration not found: ${chartId}`);
          return null;
        }
        throw new Error(`Failed to fetch chart configuration: ${response.statusText}`);
      }

      const chartConfig = await response.json();
      
      // Cache the result
      this.chartCache.set(cacheKey, chartConfig);

      return chartConfig;
    } catch (error) {
      logger.error('Error fetching chart configuration:', error);
      return null;
    }
  }

  /**
   * Retrieve tool data by data_id from session storage
   * Used for context-optimized charts that reference data by ID
   */
  async getToolData(
    sessionId: string,
    dataId: string,
    userEmail: string,
    token: string
  ): Promise<ToolDataResponse | null> {
    try {
      // Check cache first
      const cacheKey = `${sessionId}:${dataId}`;
      if (this.toolDataCache.has(cacheKey)) {
        return this.toolDataCache.get(cacheKey)!;
      }

      // Get user UUID from email
      const userResponse = await fetch(`${this.baseUrl}/user/email/${encodeURIComponent(userEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!userResponse.ok) {
        logger.error('Failed to get user from database');
        return null;
      }

      const userData = await userResponse.json();
      const userUuid = userData.uuid;

      // Fetch tool data
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/data/${dataId}?user_id=${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`Tool data not found: ${dataId}`);
          return null;
        }
        throw new Error(`Failed to fetch tool data: ${response.statusText}`);
      }

      const result = await response.json();
      const rawToolData = result.data as ToolDataResponse;

      // Normalize date keys (handles 'date' -> 'dates' for LLM resilience)
      const toolData = normalizeToolData(rawToolData);

      // Cache the normalized result
      this.toolDataCache.set(cacheKey, toolData);

      return toolData;
    } catch (error) {
      logger.error('Error fetching tool data:', error);
      return null;
    }
  }

  /**
   * Clear chart cache for a specific session
   */
  clearSessionCache(sessionId: string): void {
    // Clear chart cache
    const chartKeysToDelete = Array.from(this.chartCache.keys()).filter(key =>
      key.startsWith(`${sessionId}:`)
    );
    chartKeysToDelete.forEach(key => this.chartCache.delete(key));

    // Clear tool data cache
    const dataKeysToDelete = Array.from(this.toolDataCache.keys()).filter(key =>
      key.startsWith(`${sessionId}:`)
    );
    dataKeysToDelete.forEach(key => this.toolDataCache.delete(key));
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.chartCache.clear();
    this.toolDataCache.clear();
  }

  /**
   * Check if a chart reference is valid (has chart_id)
   */
  isValidChartReference(embeddedChart: any): embeddedChart is ChartReference {
    return embeddedChart && typeof embeddedChart === 'object' && 'chart_id' in embeddedChart;
  }

  /**
   * Check if embedded chart data is a direct configuration (backward compatibility)
   */
  isDirectChartConfig(embeddedChart: any): embeddedChart is ChartConfig {
    return embeddedChart &&
           typeof embeddedChart === 'object' &&
           ('symbol' in embeddedChart || 'chart_type' in embeddedChart) &&
           !('chart_id' in embeddedChart);
  }

  /**
   * Check if chart config is a Custom 2D chart (analytics/time series data)
   */
  isCustom2DChart(chartConfig: ChartConfig | null): chartConfig is Custom2DChartConfig {
    return chartConfig !== null &&
           'chart_type' in chartConfig &&
           chartConfig.chart_type === 'custom_2d' &&
           ('data' in chartConfig || 'data_id' in chartConfig);
  }

  /**
   * Check if chart needs data fetching (has data_id list)
   */
  needsDataFetch(chartConfig: Custom2DChartConfig): boolean {
    return 'data_id' in chartConfig &&
           Array.isArray(chartConfig.data_id) &&
           chartConfig.data_id.length > 0;
  }

  /**
   * Check if chart config is a TradingView chart (symbol-based)
   */
  isTradingViewChart(chartConfig: ChartConfig | null): chartConfig is TradingViewChartConfig {
    if (chartConfig === null) return false;
    // TradingView charts have 'symbol' and are NOT custom_2d or vol_surface_3d type
    return 'symbol' in chartConfig &&
           !this.isCustom2DChart(chartConfig) &&
           !this.isVolSurface3DChart(chartConfig);
  }

  /**
   * Check if chart config is a 3D Volatility Surface chart
   * Supports both embedded data (legacy) and data_id reference (context optimized)
   */
  isVolSurface3DChart(chartConfig: ChartConfig | null): chartConfig is VolSurface3DChartConfig {
    return chartConfig !== null &&
           'chart_type' in chartConfig &&
           chartConfig.chart_type === 'vol_surface_3d' &&
           ('data' in chartConfig || 'data_id' in chartConfig) &&
           'axis_type' in chartConfig;
  }

  /**
   * Check if chart config is a Regression chart
   * Always uses data_id reference (context optimized)
   */
  isRegressionChart(chartConfig: ChartConfig | null): chartConfig is RegressionChartConfig {
    return chartConfig !== null &&
           'chart_type' in chartConfig &&
           chartConfig.chart_type === 'regression' &&
           'data_id' in chartConfig;
  }

  /**
   * Check if 3D vol surface chart uses data_id reference (context optimized)
   */
  isVolSurface3DDataIdChart(chartConfig: VolSurface3DChartConfig): boolean {
    return 'data_id' in chartConfig && chartConfig.data_id !== undefined;
  }

  /**
   * Fetch 3D vol surface data by data_id
   */
  async getVolSurfaceData(
    sessionId: string,
    dataId: string,
    userEmail: string,
    token: string
  ): Promise<VolSurfaceToolDataResponse | null> {
    const cacheKey = `${sessionId}:${dataId}`;

    // Check cache first
    const cached = this.toolDataCache.get(cacheKey);
    if (cached) {
      return cached as unknown as VolSurfaceToolDataResponse;
    }

    try {
      // First, get user UUID from email
      const userResponse = await fetch(
        `${this.baseUrl}/user/email/${encodeURIComponent(userEmail)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!userResponse.ok) {
        logger.error('Failed to get user:', userResponse.statusText);
        return null;
      }

      const userData = await userResponse.json();
      const userUuid = userData.uuid;

      // Fetch tool data (vol surface data is stored as tool output)
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/data/${dataId}?user_id=${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`Vol surface data not found: ${dataId}`);
          return null;
        }
        throw new Error(`Failed to fetch vol surface data: ${response.statusText}`);
      }

      const result = await response.json();
      const volSurfaceData = result.data as VolSurfaceToolDataResponse;

      // Cache the result (store as unknown to allow both types in cache)
      this.toolDataCache.set(cacheKey, volSurfaceData as unknown as ToolDataResponse);

      return volSurfaceData;
    } catch (error) {
      logger.error('Error fetching vol surface data:', error);
      return null;
    }
  }

  /**
   * Fetch regression data by data_id
   * Regression data is stored as tool output from compute_linear_regression
   */
  async getRegressionData(
    sessionId: string,
    dataId: string,
    userEmail: string,
    token: string
  ): Promise<RegressionData | null> {
    const cacheKey = `${sessionId}:${dataId}`;

    // Check cache first
    const cached = this.toolDataCache.get(cacheKey);
    if (cached) {
      return cached as unknown as RegressionData;
    }

    try {
      // Get user UUID from email
      const userResponse = await fetch(
        `${this.baseUrl}/user/email/${encodeURIComponent(userEmail)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!userResponse.ok) {
        logger.error('Failed to get user:', userResponse.statusText);
        return null;
      }

      const userData = await userResponse.json();
      const userUuid = userData.uuid;

      // Fetch regression data (stored as tool output)
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/data/${dataId}?user_id=${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`Regression data not found: ${dataId}`);
          return null;
        }
        throw new Error(`Failed to fetch regression data: ${response.statusText}`);
      }

      const result = await response.json();
      const regressionData = result.data as RegressionData;

      // Cache the result
      this.toolDataCache.set(cacheKey, regressionData as unknown as ToolDataResponse);

      return regressionData;
    } catch (error) {
      logger.error('Error fetching regression data:', error);
      return null;
    }
  }

  /**
   * Fetch multiple tool data sources by data_ids
   * Returns array of { data, label } for each successfully fetched data source
   */
  async getMultipleToolData(
    sessionId: string,
    dataIds: string[],
    seriesLabels: string[] | undefined,
    userEmail: string,
    token: string
  ): Promise<Array<{ data: ToolDataResponse; label: string }>> {
    const results: Array<{ data: ToolDataResponse; label: string }> = [];

    logger.info('ChartService.getMultipleToolData: Starting fetch for', dataIds.length, 'data_ids', dataIds);

    // Fetch all data sources in parallel
    const fetchPromises = dataIds.map(async (dataId, index) => {
      const data = await this.getToolData(sessionId, dataId, userEmail, token);
      const label = seriesLabels?.[index] || `Series ${index + 1}`;
      logger.info(`ChartService.getMultipleToolData: Fetched ${dataId} (${label}):`, data ? 'SUCCESS' : 'FAILED');
      if (data) {
        return { data, label };
      }
      return null;
    });

    const fetchedData = await Promise.all(fetchPromises);

    // Filter out failed fetches
    for (const item of fetchedData) {
      if (item) {
        results.push(item);
      }
    }

    logger.info('ChartService.getMultipleToolData: Returning', results.length, 'data sources with labels:', results.map(r => r.label));

    return results;
  }

  /**
   * Merge multiple data sources by date into a unified format for multi-trace charts.
   * Each value column from each data source becomes a separate trace.
   *
   * - Single data_id with one value column: trace labeled as series_label
   * - Single data_id with multiple value columns: traces labeled as "label - columnName"
   * - Multiple data_ids: each value column from each becomes a trace
   *
   * @param dataSources Array of { data, label } from getMultipleToolData
   * @returns Merged data with Dates and multiple value columns
   */
  mergeDataByDate(
    dataSources: Array<{ data: ToolDataResponse; label: string }>
  ): { Dates: string[]; [key: string]: string[] | number[] } {
    logger.info('ChartService.mergeDataByDate: Merging', dataSources.length, 'data sources');

    if (dataSources.length === 0) {
      return { Dates: [] };
    }

    // Collect all unique dates (data should already be normalized via getToolData)
    const allDatesSet = new Set<string>();
    for (const { data } of dataSources) {
      const dates = data.dates || data.Dates || [];
      dates.forEach(d => allDatesSet.add(d));
    }

    // Sort dates
    const allDates = Array.from(allDatesSet).sort();

    // Build result with Dates
    const result: { Dates: string[]; [key: string]: string[] | number[] } = {
      Dates: allDates
    };

    for (const { data, label } of dataSources) {
      const dates = data.dates || data.Dates || [];

      // Get all value columns (non-reserved array keys)
      // Uses shared DATE_RESERVED_KEYS which includes 'date' singular for LLM resilience
      const valueKeys = Object.keys(data).filter(
        key => !DATE_RESERVED_KEYS.includes(key) && Array.isArray(data[key])
      );

      logger.info(`ChartService.mergeDataByDate: Processing "${label}" - dates: ${dates.length}, valueKeys: [${valueKeys.join(', ')}]`);

      // Process each value column as a separate trace
      for (const valueKey of valueKeys) {
        const values = data[valueKey] as number[];

        // Create a map from date to value
        const dateValueMap = new Map<string, number>();
        dates.forEach((date: string, i: number) => {
          if (values[i] !== undefined && values[i] !== null) {
            dateValueMap.set(date, values[i]);
          }
        });

        // Fill values array aligned with allDates
        const alignedValues: (number | null)[] = allDates.map(date =>
          dateValueMap.has(date) ? dateValueMap.get(date)! : null
        );

        // Determine trace label:
        // - If only one value column, use the series label directly
        // - If multiple value columns, use "label - columnName"
        const traceLabel = valueKeys.length === 1 ? label : `${label} - ${valueKey}`;
        result[traceLabel] = alignedValues as number[];
      }
    }

    logger.info('ChartService.mergeDataByDate: Result keys:', Object.keys(result));

    return result;
  }

  /**
   * Get the value key from tool data (first non-reserved key with array values).
   * Uses shared DATE_RESERVED_KEYS which includes 'date' singular for LLM resilience.
   */
  getToolDataValueKey(data: ToolDataResponse): string | null {
    for (const key of Object.keys(data)) {
      if (!DATE_RESERVED_KEYS.includes(key) && Array.isArray(data[key])) {
        return key;
      }
    }
    return null;
  }
}